import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiRefreshCw } from 'react-icons/fi';
import {
  getViolationsReport, getSlaReport, getSpendReport, getAuditLog,
} from '../../utils/expenseApi';

const SEVERITIES = ['', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

function ViolationsTab() {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [severity, setSeverity] = useState('');
  const [resolved, setResolved] = useState('');
  const [page,     setPage]     = useState(1);

  const PAGE_SIZE = 50;

  const load = async (p = page, sev = severity, res = resolved) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (sev) params.severity = sev;
      if (res !== '') params.is_resolved = res === 'true';
      setData(await getViolationsReport(params));
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [severity, resolved]);

  const pages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  return (
    <div>
      <div className="exp-filter-bar">
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--e-muted)' }}>Severity:</span>
        {SEVERITIES.map(s => (
          <motion.button key={s || 'all'} className={`exp-btn xs ${severity === s ? 'primary' : ''}`} onClick={() => { setSeverity(s); setPage(1); }} whileTap={{ scale: 0.96 }}>
            {s || 'All'}
          </motion.button>
        ))}
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--e-muted)', marginLeft: 8 }}>Resolved:</span>
        {[['', 'All'], ['false', 'Open'], ['true', 'Resolved']].map(([v, l]) => (
          <motion.button key={l} className={`exp-btn xs ${resolved === v ? 'primary' : ''}`} onClick={() => { setResolved(v); setPage(1); }} whileTap={{ scale: 0.96 }}>{l}</motion.button>
        ))}
      </div>

      {loading ? (
        <div className="exp-spinner-wrap"><div className="exp-spinner" /></div>
      ) : !data || data.items?.length === 0 ? (
        <div className="exp-empty"><div className="exp-empty-icon">✅</div><div className="exp-empty-msg">No violations found.</div></div>
      ) : (
        <>
          <div className="exp-table-wrap">
            <table className="exp-table">
              <thead>
                <tr><th>Claim</th><th>Type</th><th>Severity</th><th>Resolved</th></tr>
              </thead>
              <tbody>
                {data.items.map((v, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{v.claim_id?.slice(0, 16)}…</td>
                    <td>{v.violation_type?.replace(/_/g, ' ') || '—'}</td>
                    <td><span className={`exp-badge ${v.severity}`}>{v.severity}</span></td>
                    <td>{v.is_resolved
                      ? <span className="exp-badge APPROVED">Resolved</span>
                      : <span className="exp-badge AWAITING_APPROVAL">Open</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="exp-pagination">
              <span className="exp-page-info">{data.total} violations · Page {page} of {pages}</span>
              <div className="exp-page-btns">
                <motion.button className="exp-btn small" disabled={page === 1} onClick={() => { setPage(p => p - 1); load(page - 1); }} whileTap={{ scale: 0.96 }}>← Prev</motion.button>
                <motion.button className="exp-btn small" disabled={page === pages} onClick={() => { setPage(p => p + 1); load(page + 1); }} whileTap={{ scale: 0.96 }}>Next →</motion.button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SlaTab() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSlaReport().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="exp-spinner-wrap"><div className="exp-spinner" /></div>;
  if (!data)   return <div className="exp-empty"><div className="exp-empty-msg">Could not load SLA report.</div></div>;

  const stats = [
    { label: 'Pending Approvals',      value: data.pending_approvals,      sub: 'awaiting action' },
    { label: 'SLA Breached',           value: data.breached_sla_count,     sub: 'past deadline', cls: data.breached_sla_count > 0 ? 'err' : 'ok' },
    { label: 'SLA Breach Rate',        value: `${(data.sla_breach_rate_pct || 0).toFixed(1)}%`, sub: 'of pending approvals', cls: data.sla_breach_rate_pct > 10 ? 'err' : data.sla_breach_rate_pct > 0 ? 'warn' : 'ok' },
    { label: 'Avg. Approval Time',     value: `${(data.average_approval_time_hours || 0).toFixed(1)}h`, sub: 'per approval' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: '2026-07-17', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: '0.76rem', color: 'var(--e-muted)' }}>
          Report generated: {new Date(data.report_generated_at).toLocaleString('en-IN')}
        </span>
      </div>
      <div className="exp-sla-grid">
        {stats.map(s => (
          <div key={s.label} className={`exp-stat-card ${s.cls || ''}`}>
            <div className="exp-stat-label">{s.label}</div>
            <div className="exp-stat-value">{s.value}</div>
            <div className="exp-stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {data.pending_by_tier && Object.keys(data.pending_by_tier).length > 0 && (
        <div className="exp-card" style={{ marginTop: 16 }}>
          <div className="exp-card-header"><span className="exp-card-title">Pending by Tier</span></div>
          <div className="exp-card-body">
            {Object.entries(data.pending_by_tier).map(([tier, count]) => {
              const max = Math.max(...Object.values(data.pending_by_tier));
              return (
                <div key={tier} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.84rem', fontWeight: 700 }}>
                    <span>Tier {tier}</span>
                    <span style={{ color: 'var(--ep)' }}>{count}</span>
                  </div>
                  <div className="exp-spend-bar">
                    <div className="exp-spend-fill" style={{ width: `${max > 0 ? (count / max) * 100 : 0}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SpendTab() {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');
  const [companyCode, setCompanyCode] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom)    params.date_from    = dateFrom;
      if (dateTo)      params.date_to      = dateTo;
      if (companyCode) params.company_code = companyCode;
      setData(await getSpendReport(params));
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const maxSpend = data?.breakdown
    ? Math.max(...data.breakdown.map(b => b.total_spend))
    : 1;

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-end' }}>
        <div className="exp-field">
          <label className="exp-label">From</label>
          <input type="date" className="exp-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: 160 }} />
        </div>
        <div className="exp-field">
          <label className="exp-label">To</label>
          <input type="date" className="exp-input" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: 160 }} />
        </div>
        <div className="exp-field">
          <label className="exp-label">Company Code</label>
          <input className="exp-input" value={companyCode} onChange={e => setCompanyCode(e.target.value)} placeholder="1000" style={{ width: 120 }} />
        </div>
        <motion.button className="exp-btn primary small" onClick={load} whileTap={{ scale: 0.96 }} style={{ alignSelf: 'flex-end' }}>
          Apply
        </motion.button>
      </div>

      {loading ? (
        <div className="exp-spinner-wrap"><div className="exp-spinner" /></div>
      ) : !data ? (
        <div className="exp-empty"><div className="exp-empty-msg">Could not load spend report.</div></div>
      ) : (
        <>
          <div className="exp-stats" style={{ marginBottom: 14 }}>
            <div className="exp-stat-card">
              <div className="exp-stat-label">Total Claims</div>
              <div className="exp-stat-value">{data.total_claims}</div>
            </div>
            <div className="exp-stat-card">
              <div className="exp-stat-label">Total Spend</div>
              <div className="exp-stat-value" style={{ fontSize: '1.4rem' }}>
                {parseFloat(data.total_spend).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
            </div>
          </div>

          {data.breakdown?.length > 0 && (
            <div className="exp-card">
              <div className="exp-card-header"><span className="exp-card-title">Spend Breakdown</span></div>
              <div className="exp-card-body">
                {data.breakdown.map((b, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.84rem' }}>
                      <span>
                        <span className={`exp-badge ${b.status}`} style={{ marginRight: 8 }}>{b.status}</span>
                        {b.company_code} · {b.currency_code}
                        <span style={{ marginLeft: 8, color: 'var(--e-muted)', fontSize: '0.78rem' }}>{b.claim_count} claims</span>
                      </span>
                      <span style={{ fontWeight: 800, color: 'var(--ep)' }}>
                        {parseFloat(b.total_spend).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className="exp-spend-bar">
                      <div className="exp-spend-fill" style={{ width: `${maxSpend > 0 ? (b.total_spend / maxSpend) * 100 : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AuditLogTab() {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [agentName, setAgentName] = useState('');
  const [page,      setPage]      = useState(1);

  const PAGE_SIZE = 50;

  const load = async (p = page, agent = agentName) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: PAGE_SIZE };
      if (agent) params.agent_name = agent;
      setData(await getAuditLog(params));
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [agentName]);

  const pages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;
  const AGENTS = ['', 'PolicyAgent', 'SAPPostingAgent', 'ApprovalRoutingAgent'];

  return (
    <div>
      <div className="exp-filter-bar">
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--e-muted)' }}>Agent:</span>
        {AGENTS.map(a => (
          <motion.button key={a || 'all'} className={`exp-btn xs ${agentName === a ? 'primary' : ''}`} onClick={() => { setAgentName(a); setPage(1); }} whileTap={{ scale: 0.96 }}>
            {a || 'All'}
          </motion.button>
        ))}
      </div>

      {loading ? (
        <div className="exp-spinner-wrap"><div className="exp-spinner" /></div>
      ) : !data || data.items?.length === 0 ? (
        <div className="exp-empty"><div className="exp-empty-msg">No audit entries found.</div></div>
      ) : (
        <>
          <div className="exp-table-wrap">
            <table className="exp-table">
              <thead>
                <tr><th>Agent</th><th>Action</th><th>Result</th><th>Rule Cited</th><th>Duration</th><th>Time</th></tr>
              </thead>
              <tbody>
                {data.items.map((e, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700, color: 'var(--ep-dark)' }}>{e.agent_name}</td>
                    <td>
                      <div>{e.action}</div>
                      {e.detail && <div style={{ fontSize: '0.75rem', color: 'var(--e-muted)', marginTop: 1 }}>{e.detail}</div>}
                    </td>
                    <td>
                      <span className={`exp-badge ${e.result === 'SUCCESS' ? 'APPROVED' : 'REJECTED'}`}>
                        {e.result}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--e-muted)' }}>{e.policy_rule_cited || '—'}</td>
                    <td style={{ color: 'var(--e-muted)' }}>{e.duration_ms != null ? `${e.duration_ms} ms` : '—'}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--e-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(e.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="exp-pagination">
              <span className="exp-page-info">{data.total} entries · Page {page} of {pages}</span>
              <div className="exp-page-btns">
                <motion.button className="exp-btn small" disabled={page === 1} onClick={() => { setPage(p => p - 1); load(page - 1); }} whileTap={{ scale: 0.96 }}>← Prev</motion.button>
                <motion.button className="exp-btn small" disabled={page === pages} onClick={() => { setPage(p => p + 1); load(page + 1); }} whileTap={{ scale: 0.96 }}>Next →</motion.button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const REPORT_TABS = [
  { id: 'violations', label: 'Violations' },
  { id: 'sla',        label: 'SLA' },
  { id: 'spend',      label: 'Spend' },
  { id: 'audit',      label: 'Audit Log' },
];

export default function ExpenseReports() {
  const [tab, setTab] = useState('violations');

  return (
    <motion.div className="exp-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <h2 className="exp-section-title">Reports</h2>

      <div className="exp-report-tabs">
        {REPORT_TABS.map(t => (
          <button key={t.id} className={`exp-report-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'violations' && <ViolationsTab />}
      {tab === 'sla'        && <SlaTab />}
      {tab === 'spend'      && <SpendTab />}
      {tab === 'audit'      && <AuditLogTab />}
    </motion.div>
  );
}
